import { motion } from 'framer-motion';
import { type MouseEvent, type ReactElement } from 'react';
import { Link, generatePath, useLocation } from 'react-router-dom';
import { ImportCurve, Eye, MoreVertical, Trash, User } from '@/components/icons';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { CardActionButton } from '@/components/system';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDateOnly } from '@/lib/date';
import { PatientDetailPage } from '@/pages/constants';
import { ReportStatusBadge } from '@/pages/patients/components/StatusBadges';
import { canExportReport, getAge, getReportLink, translateGender } from '@/utils';
import type { ReportData } from '@/types/report';

type DashboardMobileReportCardProps = {
    report: ReportData;
    onDelete: (id: ReportData['id']) => void;
    onExport: (id: ReportData['id']) => void;
};

export default function DashboardMobileReportCard({
    report,
    onDelete,
    onExport,
}: DashboardMobileReportCardProps): ReactElement {
    const location = useLocation();
    const reportStatus = report.status || 'in_progress';
    const patientId = report.patient?.id;
    const backTo = `${location.pathname}${location.search}`;
    const reportLink = getReportLink(report.id);
    const exportEnabled = canExportReport(report);
    const updatedAt = report.updated_at ? formatDateOnly(report.updated_at) : 'Sem atualização';
    const pendingConsolidationsCount = report.pending_consolidations?.length ?? 0;
    const pendingPublisherApprovalsCount = report.pending_publisher_result_approvals_count ?? 0;
    const pendingReviewCount = pendingConsolidationsCount + pendingPublisherApprovalsCount;
    const demographics = [
        report.patient?.date_of_birth ? `${getAge(report.patient.date_of_birth)} anos` : undefined,
        report.patient?.gender ? translateGender(report.patient.gender) : undefined,
        report.patient?.city,
    ]
        .filter(Boolean)
        .join(' • ');

    const handleMenuClick = (event: MouseEvent) => {
        event.stopPropagation();
    };

    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: 12 },
                visible: { opacity: 1, y: 0 },
            }}
        >
            <Card className="overflow-hidden rounded-2xl border border-border/80 bg-card/95 p-4 shadow-elevation-1 transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5 hover:border-brand/20 hover:shadow-elevation-2">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                        <PatientAvatar
                            name={report.patient?.full_name || ''}
                            avatarUrl={report.patient?.avatar_url}
                            className="size-11 rounded-xl"
                        />
                        <div className="min-w-0">
                            <h3 className="truncate text-base font-semibold text-foreground">
                                {report.patient?.full_name || 'Paciente não vinculado'}
                            </h3>
                            <p className="mt-1 text-xs text-muted-foreground">
                                Atualizado em {updatedAt}
                            </p>
                        </div>
                    </div>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={handleMenuClick}>
                            <Button variant="ghost" size="icon" className="size-10 shrink-0">
                                <MoreVertical className="size-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52" onClick={handleMenuClick}>
                            <DropdownMenuItem asChild>
                                <Link to={reportLink}>
                                    <Eye className="mr-2 size-4" />
                                    Abrir laudo
                                </Link>
                            </DropdownMenuItem>
                            {exportEnabled && (
                                <DropdownMenuItem
                                    onClick={(event: MouseEvent) => {
                                        event.preventDefault();
                                        onExport(report.id);
                                    }}
                                >
                                    <ImportCurve className="mr-2 size-4" />
                                    Baixar em .docx
                                </DropdownMenuItem>
                            )}
                            {patientId && (
                                <DropdownMenuItem asChild>
                                    <Link
                                        to={`/${generatePath(PatientDetailPage, {
                                            patientId,
                                        })}`}
                                        state={{ backTo }}
                                    >
                                        <User className="mr-2 size-4" />
                                        Abrir paciente
                                    </Link>
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                onClick={(event: MouseEvent) => {
                                    event.preventDefault();
                                    onDelete(report.id);
                                }}
                                className="text-red-600 focus:text-red-600"
                            >
                                <Trash className="mr-2 size-4" />
                                Excluir
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <ReportStatusBadge status={reportStatus} />
                    {demographics && (
                        <span className="rounded-full bg-surface-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                            {demographics}
                        </span>
                    )}
                    {pendingReviewCount > 0 && (
                        <span className="rounded-full border border-warning/20 bg-warning/15 px-2.5 py-1 text-xs font-semibold tabular-nums text-warning">
                            {pendingReviewCount} resultado
                            {pendingReviewCount > 1 ? 's' : ''} p/ revisão
                        </span>
                    )}
                </div>

                <CardActionButton to={reportLink} className="mt-4">
                    Abrir laudo
                </CardActionButton>
            </Card>
        </motion.div>
    );
}
